import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Button, Card, EmptyState, Skeleton } from '@/ui/primitives'
import { Camp } from '@/ui/form'
import { IncarcaImagine } from '@/ui/incarca-imagine'

/* ─────────────────── 1. Documente ─────────────────── */

/** Sabloanele de document care exista efectiv in aplicatie. Previzualizarea se face pe o
 *  rezervare REALA (cea mai recenta), ca sa se vada cum arata cu date, nu cu „lorem". */
const DOCUMENTE = [
  { tip: 'voucher', nume: 'Voucher turistic', descriere: 'Se prezinta la hotel sau la furnizor.' },
  { tip: 'contract-tourist', nume: 'Contract cu turistul', descriere: 'Contractul complet, conform OG 2/2018.' },
  { tip: 'bon-comanda', nume: 'Bon de comanda', descriere: 'Comanda semnata de client, cu serviciile.' },
  { tip: 'confirmation', nume: 'Confirmare rezervare', descriere: 'Confirmarea trimisa clientului.' },
  { tip: 'program', nume: 'Program de calatorie', descriere: 'Programul pe zile — se poate si scrie de mana.' },
  { tip: 'decont', nume: 'Decont comanda', descriere: 'Facturi si plati, pentru dosar.' },
  { tip: 'contract', nume: 'Contract servicii (vechi)', descriere: 'Varianta simpla, pastrata pentru dosarele vechi.' },
]

type RezScurta = { id: number; number: string; partner: string | null }

function Documente() {
  const [eroare, setEroare] = useState<string | null>(null)
  const [lucreaza, setLucreaza] = useState<string | null>(null)

  const { data } = useQuery({
    queryKey: ['sablon-rezervare'],
    queryFn: () => api.get<{ reservations: RezScurta[] }>('/reservations/list-fast',
      { limit: 1, active_only: true, sort: 'begin_date', sort_dir: 'asc' }),
  })
  const rez = data?.reservations?.[0]

  const deschide = async (tip: string, previzualizare: boolean) => {
    if (!rez) return
    setEroare(null); setLucreaza(tip + previzualizare)
    try {
      await api.deschideFisier(`/documents/${rez.id}/${tip}/${previzualizare ? 'preview' : 'pdf'}`)
    } catch (e) {
      setEroare(e instanceof Error ? e.message : 'Nu am putut deschide documentul')
    } finally { setLucreaza(null) }
  }

  return (
    <div className="px-8 py-6 space-y-4">
      <Card className="p-4">
        <p className="text-[13.5px]">
          Documentele se genereaza din datele rezervarii, cu antetul si culorile din tabul
          „Antet si identitate".{' '}
          {rez
            ? <>Exemplele de mai jos se deschid pe rezervarea <strong>{rez.number}</strong>
                {rez.partner ? ` (${rez.partner})` : ''}.</>
            : 'Nu am gasit o rezervare activa pentru exemplu.'}
        </p>
      </Card>

      <div className="grid gap-3 md:grid-cols-2">
        {DOCUMENTE.map((d) => (
          <Card key={d.tip} className="p-5">
            <p className="text-[14.5px] font-medium">{d.nume}</p>
            <p className="mt-1 text-[13px] text-muted">{d.descriere}</p>
            <div className="mt-3 flex gap-2">
              <Button onClick={() => deschide(d.tip, true)} disabled={!rez || lucreaza === d.tip + true}>
                {lucreaza === d.tip + true ? 'Se deschide…' : 'Vezi exemplu'}
              </Button>
              <Button onClick={() => deschide(d.tip, false)} disabled={!rez || lucreaza === d.tip + false}>
                {lucreaza === d.tip + false ? 'Se genereaza…' : 'PDF'}
              </Button>
            </div>
          </Card>
        ))}
      </div>
      {eroare && <p className="text-[13px] text-bad">{eroare}</p>}
    </div>
  )
}

/* ─────────────────── 2. Antet si identitate ─────────────────── */

type Setari = {
  id: number; company_name: string; company_cui: string; company_reg_com: string
  company_address: string; company_city: string; company_county: string
  company_phone: string; company_email: string; company_website: string
  company_iban: string; company_bank: string
  license_number: string; license_date: string; insolvency_policy: string
  emergency_phone: string; footer_text: string
  primary_color: string; secondary_color: string; accent_color: string; text_color: string
  font_family: string; font_size_base: number
  logo_path: string | null; logo_width: number | null; logo_height: number | null
  signature_path: string | null; stamp_path: string | null
  show_logo: boolean; show_license: boolean; show_bank_details: boolean
  show_emergency_contact: boolean; show_footer: boolean
  show_signature: boolean; show_stamp: boolean
  name: string; is_active: boolean; is_default: boolean
}

const CAMPURI: { cheie: keyof Setari; eticheta: string; grup: string }[] = [
  { cheie: 'company_name', eticheta: 'Denumire', grup: 'Firma' },
  { cheie: 'company_cui', eticheta: 'CUI', grup: 'Firma' },
  { cheie: 'company_reg_com', eticheta: 'Nr. reg. com.', grup: 'Firma' },
  { cheie: 'company_address', eticheta: 'Adresa', grup: 'Firma' },
  { cheie: 'company_city', eticheta: 'Oras', grup: 'Firma' },
  { cheie: 'company_county', eticheta: 'Judet', grup: 'Firma' },
  { cheie: 'company_phone', eticheta: 'Telefon', grup: 'Contact' },
  { cheie: 'company_email', eticheta: 'Email', grup: 'Contact' },
  { cheie: 'company_website', eticheta: 'Site', grup: 'Contact' },
  { cheie: 'emergency_phone', eticheta: 'Telefon de urgenta', grup: 'Contact' },
  { cheie: 'company_bank', eticheta: 'Banca', grup: 'Bancar' },
  { cheie: 'company_iban', eticheta: 'IBAN', grup: 'Bancar' },
  { cheie: 'license_number', eticheta: 'Licenta de turism', grup: 'Licenta' },
  { cheie: 'license_date', eticheta: 'Data licentei', grup: 'Licenta' },
  { cheie: 'insolvency_policy', eticheta: 'Polita de insolventa', grup: 'Licenta' },
  { cheie: 'footer_text', eticheta: 'Text de subsol', grup: 'Aspect' },
]

const CULORI: { cheie: keyof Setari; eticheta: string }[] = [
  { cheie: 'primary_color', eticheta: 'Principala' },
  { cheie: 'secondary_color', eticheta: 'Secundara' },
  { cheie: 'accent_color', eticheta: 'Accent' },
  { cheie: 'text_color', eticheta: 'Textul' },
]

/** Fonturile pe care le are generatorul de PDF instalat pe server. */
const FONTURI = ['DejaVu Sans', 'Helvetica', 'Times New Roman', 'Courier']

const BIFE: { cheie: keyof Setari; eticheta: string }[] = [
  { cheie: 'show_logo', eticheta: 'Arata sigla' },
  { cheie: 'show_license', eticheta: 'Arata licenta de turism' },
  { cheie: 'show_bank_details', eticheta: 'Arata contul bancar' },
  { cheie: 'show_emergency_contact', eticheta: 'Arata telefonul de urgenta' },
  { cheie: 'show_footer', eticheta: 'Arata subsolul' },
  { cheie: 'show_signature', eticheta: 'Arata semnatura' },
  { cheie: 'show_stamp', eticheta: 'Arata stampila' },
]

function Antet() {
  const qc = useQueryClient()
  const [f, setF] = useState<Partial<Setari>>({})
  const [salvat, setSalvat] = useState(false)
  const [eroare, setEroare] = useState<string | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['sabloane-antet'],
    queryFn: () => api.get<{ settings: Setari }>('/template-settings/'),
  })
  useEffect(() => { if (data?.settings) setF(data.settings) }, [data])

  const salveaza = useMutation({
    mutationFn: () => api.put(`/template-settings/${data!.settings.id}`, f),
    onSuccess: () => { setSalvat(true); qc.invalidateQueries({ queryKey: ['sabloane-antet'] }) },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  if (isPending) return <div className="px-8 py-6"><Card><Skeleton rows={8} /></Card></div>
  if (!data?.settings) return <div className="px-8 py-6"><Card><EmptyState title="Nu exista setari de sablon" /></Card></div>

  const grupuri = [...new Set(CAMPURI.map((c) => c.grup))]

  return (
    <div className="px-8 py-6 space-y-5">
      <Card className="p-4">
        <p className="text-[13.5px] text-muted">
          Datele astea apar pe TOATE documentele: voucher, contract, bon de comanda, factura,
          programul calatoriei. Se schimba o data, se vad peste tot.
        </p>
      </Card>

      {grupuri.map((g) => (
        <Card key={g} className="p-5">
          <p className="text-[12.5px] uppercase tracking-[0.08em] text-muted mb-3">{g}</p>
          <div className="grid gap-4 sm:grid-cols-3">
            {CAMPURI.filter((c) => c.grup === g).map((c) => (
              <Camp key={c.cheie} eticheta={c.eticheta} id={`s-${c.cheie}`}
                valoare={String(f[c.cheie] ?? '')}
                seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, [c.cheie]: v })) }} />
            ))}
          </div>
        </Card>
      ))}

      <Card className="p-5">
        <p className="text-[12.5px] uppercase tracking-[0.08em] text-muted mb-3">Sigla</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <IncarcaImagine eticheta="Fisierul siglei"
            sursa={data.settings.logo_path ? `/api/template-settings/logo/${data.settings.id}` : null}
            cale="/template-settings/upload-logo" camp="logo"
            campuri={{ settings_id: String(data.settings.id) }}
            indiciu="PNG, JPG, GIF sau SVG"
            gata={() => qc.invalidateQueries({ queryKey: ['sabloane-antet'] })} />
          <Camp eticheta="Latime (px)" id="s-logo-w" tip="number" valoare={String(f.logo_width ?? '')}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, logo_width: Number(v) })) }} />
          <Camp eticheta="Inaltime (px)" id="s-logo-h" tip="number" valoare={String(f.logo_height ?? '')}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, logo_height: Number(v) })) }}
            indiciu="Lasa gol pentru inaltime proportionala" />
        </div>
      </Card>

      <Card className="p-5">
        <p className="text-[12.5px] uppercase tracking-[0.08em] text-muted mb-3">Culori si scris</p>
        <div className="grid gap-4 sm:grid-cols-4">
          {CULORI.map((c) => (
            <div key={c.cheie}>
              <label htmlFor={`s-${c.cheie}`} className="text-[12.5px] text-muted mb-1 block">{c.eticheta}</label>
              <div className="flex items-center gap-2">
                <input id={`s-${c.cheie}`} type="color" value={String(f[c.cheie] ?? '#000000')}
                  onChange={(e) => { setSalvat(false); setF((x) => ({ ...x, [c.cheie]: e.target.value })) }}
                  className="h-9 w-12 rounded-[var(--radius-control)] border border-line bg-surface" />
                <span className="text-[12.5px] text-muted tnum">{String(f[c.cheie] ?? '')}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 max-w-xl">
          <Camp eticheta="Scrisul documentelor" id="s-font" tip="select" valoare={String(f.font_family ?? 'DejaVu Sans')}
            optiuni={FONTURI.map((x) => ({ v: x, t: x }))}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, font_family: v })) }} />
          <Camp eticheta="Marimea de baza (pt)" id="s-font-size" tip="number" valoare={String(f.font_size_base ?? 9)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, font_size_base: Number(v) })) }} />
        </div>
      </Card>

      <Card className="p-5">
        <p className="text-[12.5px] uppercase tracking-[0.08em] text-muted mb-3">Semnatura si stampila</p>
        <div className="grid gap-5 sm:grid-cols-2">
          {/* Serverul are endpoint de citire DOAR pentru sigla (`/logo/{id}`), nu si pentru
              semnatura sau stampila — asa ca aici se spune in cuvinte daca exista fisier,
              in loc sa se ceara o imagine care ar raspunde 404. */}
          <IncarcaImagine eticheta="Semnatura scanata" sursa={null}
            cale="/template-settings/upload-signature" camp="signature"
            campuri={{ signature_type: 'signature', settings_id: String(data.settings.id) }}
            indiciu={data.settings.signature_path ? 'exista o semnatura incarcata' : 'nicio semnatura incarcata'}
            gata={() => qc.invalidateQueries({ queryKey: ['sabloane-antet'] })} />
          <IncarcaImagine eticheta="Stampila scanata" sursa={null}
            cale="/template-settings/upload-signature" camp="signature"
            campuri={{ signature_type: 'stamp', settings_id: String(data.settings.id) }}
            indiciu={data.settings.stamp_path ? 'exista o stampila incarcata' : 'nicio stampila incarcata'}
            gata={() => qc.invalidateQueries({ queryKey: ['sabloane-antet'] })} />
        </div>
        <p className="mt-3 text-[12px] text-muted">
          Apar pe documente doar daca sunt bifate mai jos.
        </p>
      </Card>

      <Card className="p-5">
        <p className="text-[12.5px] uppercase tracking-[0.08em] text-muted mb-3">Ce se afiseaza</p>
        <div className="grid gap-2.5 sm:grid-cols-2">
          {BIFE.map((b) => (
            <label key={b.cheie} className="flex items-center gap-2.5 text-[14px]">
              <input type="checkbox" checked={Boolean(f[b.cheie])}
                onChange={(e) => { setSalvat(false); setF((x) => ({ ...x, [b.cheie]: e.target.checked })) }} />
              {b.eticheta}
            </label>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Button variant="primary" onClick={() => { setEroare(null); salveaza.mutate() }}
          disabled={salveaza.isPending}>
          {salveaza.isPending ? 'Se salveaza…' : 'Salveaza'}
        </Button>
        {salvat && <span className="text-[13px] text-good">Salvat — apare pe documentele urmatoare</span>}
        {eroare && <span className="text-[13px] text-bad">{eroare}</span>}
      </div>
    </div>
  )
}

/* ─────────────────── 3. Conditii de anulare ─────────────────── */

type Regula = { from_days: number | null; to_days: number | null; percent: number | null
                fixed_amount: number | null; description: string | null }
type Penalizare = {
  id: number; name: string; description: string | null; rules: Regula[]
  special_conditions: string | null; is_default: boolean; is_active: boolean
}

function Penalizari() {
  const { data, isPending } = useQuery({
    queryKey: ['penalizari'],
    queryFn: () => api.get<{ templates: Penalizare[]; total: number }>('/penalty-templates'),
  })

  if (isPending) return <div className="px-8 py-6"><Card><Skeleton rows={6} /></Card></div>
  const sabloane = data?.templates ?? []

  return (
    <div className="px-8 py-6 space-y-4">
      <Card className="p-4">
        <p className="text-[13.5px] text-muted">
          Se aleg pe rezervare si intra in contractul cu turistul. Procentul se aplica la
          valoarea rezervarii, in functie de cate zile mai sunt pana la plecare.
        </p>
      </Card>

      {sabloane.length === 0 && <Card><EmptyState title="Niciun sablon activ" /></Card>}

      {sabloane.map((p) => (
        <Card key={p.id} className="p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <p className="text-[15px] font-medium">{p.name}</p>
            {p.is_default && <Badge tone="good">implicit</Badge>}
            {!p.is_active && <Badge tone="neutral">inactiv</Badge>}
            <p className="text-[13px] text-muted">{p.description}</p>
          </div>
          <table className="mt-3 w-full text-[13.5px]">
            <tbody>
              {(p.rules ?? []).map((r, i) => (
                <tr key={i} className="border-t border-line first:border-0">
                  <td className="py-2 w-56 tnum text-muted">
                    {r.to_days == null ? `peste ${(r.from_days ?? 0) - 1} zile`
                      : r.from_days == null ? `sub ${r.to_days} zile`
                      : `${r.from_days}–${r.to_days} zile`}
                  </td>
                  <td className="py-2 tnum w-24">
                    {r.percent != null ? `${r.percent}%` : r.fixed_amount != null ? `${r.fixed_amount} lei` : '—'}
                  </td>
                  <td className="py-2 text-muted">{r.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {p.special_conditions && (
            <p className="mt-3 text-[12.5px] text-muted italic">{p.special_conditions}</p>
          )}
        </Card>
      ))}
    </div>
  )
}

/* ─────────────────── 4. SMS si email ─────────────────── */

type Sms = {
  sms_enabled: boolean; auto_reminder_enabled: boolean; reminder_hour: string
  sms_type: string; reminder_template: string; last_run: string | null
  last_run_result: string | null; credit: number
}

function Mesaje() {
  const qc = useQueryClient()
  const [f, setF] = useState<Partial<Sms>>({})
  const [salvat, setSalvat] = useState(false)
  const [eroare, setEroare] = useState<string | null>(null)
  const [telefon, setTelefon] = useState('')
  const [rezultatTest, setRezultatTest] = useState<string | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['sms-setari'], queryFn: () => api.get<Sms>('/sms-settings/'),
  })
  useEffect(() => { if (data) setF(data) }, [data])

  const salveaza = useMutation({
    mutationFn: () => api.put('/sms-settings/', {
      sms_enabled: Boolean(f.sms_enabled),
      auto_reminder_enabled: Boolean(f.auto_reminder_enabled),
      reminder_hour: f.reminder_hour ?? '09:00',
      sms_type: f.sms_type ?? '',
      reminder_template: f.reminder_template ?? '',
    }),
    onSuccess: () => { setSalvat(true); qc.invalidateQueries({ queryKey: ['sms-setari'] }) },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  const testeaza = useMutation({
    mutationFn: () => api.post<{ success: boolean; message?: string }>('/sms-settings/test', {
      phone: telefon, message: f.reminder_template ?? 'Mesaj de proba EventTour',
    }),
    onSuccess: (r) => setRezultatTest(r.success ? `Trimis la ${telefon}` : (r.message ?? 'Nu s-a putut trimite')),
    onError: (e) => setRezultatTest(e instanceof Error ? e.message : 'Nu s-a putut trimite'),
  })

  if (isPending) return <div className="px-8 py-6"><Card><Skeleton rows={5} /></Card></div>
  const s = data!

  return (
    <div className="px-8 py-6 space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">SMS</p>
          <p className="mt-1 text-[15px]">{s.sms_enabled ? 'pornit' : 'oprit'}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Ultima rulare</p>
          <p className="mt-1 text-[15px]">{s.last_run ?? 'niciodata'}</p>
          {s.last_run_result && <p className="mt-0.5 text-[12px] text-muted">{s.last_run_result}</p>}
        </Card>
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Credit ramas</p>
          <p className="mt-1 tnum text-[15px]">{s.credit?.toFixed(2)}</p>
        </Card>
      </div>

      <Card className="p-5">
        <p className="text-[12.5px] uppercase tracking-[0.08em] text-muted mb-3">Reamintiri prin SMS</p>
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Camp eticheta="Ora reamintirii" id="sms-ora" valoare={String(f.reminder_hour ?? '')}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, reminder_hour: v })) }}
            indiciu="Forma HH:MM, ora serverului" />
          <Camp eticheta="Tipul mesajului" id="sms-tip" valoare={String(f.sms_type ?? '')}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sms_type: v })) }} />
          <div className="sm:col-span-2">
            <Camp eticheta="Textul reamintirii" id="sms-sablon" tip="textarea"
              valoare={String(f.reminder_template ?? '')}
              seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, reminder_template: v })) }}
              indiciu={'Locurile care se completeaza singure: {invoice}, {amount}, {currency}'} />
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-2.5 text-[14px]">
            <input type="checkbox" checked={Boolean(f.sms_enabled)}
              onChange={(e) => { setSalvat(false); setF((x) => ({ ...x, sms_enabled: e.target.checked })) }} />
            Trimiterea de SMS-uri e pornita
          </label>
          <label className="flex items-center gap-2.5 text-[14px]">
            <input type="checkbox" checked={Boolean(f.auto_reminder_enabled)}
              onChange={(e) => { setSalvat(false); setF((x) => ({ ...x, auto_reminder_enabled: e.target.checked })) }} />
            Reamintirile pleaca automat, la ora de mai sus
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button variant="primary" onClick={() => { setEroare(null); salveaza.mutate() }}
            disabled={salveaza.isPending}>
            {salveaza.isPending ? 'Se salveaza…' : 'Salveaza'}
          </Button>
          {salvat && <span className="text-[13px] text-good">Salvat</span>}
          {eroare && <span className="text-[13px] text-bad">{eroare}</span>}
        </div>
      </Card>

      <Card className="p-5">
        <p className="text-[12.5px] uppercase tracking-[0.08em] text-muted mb-3">Trimite un SMS de proba</p>
        <div className="flex flex-wrap items-end gap-3 max-w-2xl">
          <div className="w-56">
            <Camp eticheta="Numar de telefon" id="sms-test" tip="tel" valoare={telefon} seteaza={setTelefon} />
          </div>
          <Button onClick={() => { setRezultatTest(null); testeaza.mutate() }}
            disabled={testeaza.isPending || !telefon}>
            {testeaza.isPending ? 'Se trimite…' : 'Trimite proba'}
          </Button>
          {rezultatTest && <span className="text-[13px] text-muted">{rezultatTest}</span>}
        </div>
        <p className="mt-2 text-[12px] text-muted">Pleaca exact textul reamintirii de mai sus.</p>
      </Card>

      <Card className="p-4">
        <p className="text-[13px] text-muted">
          Emailurile de documente (voucher, acte de plecare) pleaca din fisa rezervarii, cu
          antetul din „Antet si identitate". Serverul de email se configureaza in{' '}
          <Link to="/setari/email" className="text-accent-ink underline">Setari → Email</Link>.
        </p>
      </Card>
    </div>
  )
}

/* ─────────────────── invelisul ─────────────────── */

export function Sabloane({ copil }: { copil: 'documente' | 'antet' | 'penalizari' | 'mesaje' }) {
  const titlu = {
    documente: 'Sabloane de documente', antet: 'Antet si identitate',
    penalizari: 'Conditii de anulare', mesaje: 'SMS si email',
  }[copil]
  return (
    <>
      <PageHeader title={titlu} />
      {copil === 'documente' && <Documente />}
      {copil === 'antet' && <Antet />}
      {copil === 'penalizari' && <Penalizari />}
      {copil === 'mesaje' && <Mesaje />}
    </>
  )
}
